/** Peso medio por botella (kg) — configurable por vino con weightKg */
export const DEFAULT_BOTTLE_WEIGHT_KG = 1;

export const SHIPPING_ZONE_LABELS = {
  huelva: 'Huelva (urbano / provincial)',
  andalucia: 'Andalucía (regional / limítrofe)',
  peninsula: 'España peninsular',
  balearic: 'Islas Baleares',
  canary: 'Islas Canarias',
  ceuta_melilla: 'Ceuta y Melilla'
};

/**
 * Tarifas transportista por banda de peso (tabla acordada).
 * urbano = Huelva | regional = Andalucía | peninsular = resto España
 */
export const SHIPPING_TARIFF_BANDS = {
  urbano: {
    label: 'Urbano / Provincial',
    upTo2Kg: 5.42,
    upTo5Kg: 5.72,
    upTo10Kg: 7.19,
    extraPerKg: 0.38
  },
  regional: {
    label: 'Regional / Limítrofe',
    upTo2Kg: 5.85,
    upTo5Kg: 6.35,
    upTo10Kg: 7.85,
    extraPerKg: 0.44
  },
  peninsular: {
    label: 'España peninsular',
    upTo2Kg: 6.34,
    upTo5Kg: 6.93,
    upTo10Kg: 8.67,
    extraPerKg: 0.5
  }
};

/** Zona de envío → tarifa de la tabla */
export const ZONE_TO_TARIFF = {
  huelva: 'urbano',
  andalucia: 'regional',
  peninsula: 'peninsular',
  balearic: 'peninsular',
  canary: 'peninsular',
  ceuta_melilla: 'peninsular'
};

const ANDALUCIA_CP_PREFIXES = ['04', '11', '14', '18', '23', '29', '41'];

export function calculateCartWeightKg(cart) {
  return cart.reduce((total, item) => {
    const unitWeight =
      Number(item.weightKg) > 0 ? Number(item.weightKg) : DEFAULT_BOTTLE_WEIGHT_KG;
    return total + item.quantity * unitWeight;
  }, 0);
}

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function isHuelvaDestination(postalCode = '', province = '') {
  const cp = String(postalCode).trim().replace(/\s/g, '');
  const prov = normalizeText(province);

  if (/^21\d{3}$/.test(cp)) return true;
  return prov === 'huelva' || prov.includes('huelva');
}

export function isAndaluciaDestination(postalCode = '', province = '') {
  if (isHuelvaDestination(postalCode, province)) return false;

  const cp = String(postalCode).trim().replace(/\s/g, '');
  if (/^\d{5}$/.test(cp)) {
    const prefix = cp.slice(0, 2);
    if (ANDALUCIA_CP_PREFIXES.includes(prefix)) return true;
  }

  const prov = normalizeText(province);
  const andaluciaProvinces = [
    'almeria',
    'cadiz',
    'cordoba',
    'granada',
    'jaen',
    'malaga',
    'sevilla',
    'andalucia'
  ];
  return andaluciaProvinces.some((name) => prov === name || prov.includes(name));
}

/**
 * Zonas: huelva | andalucia | peninsula | balearic | canary | ceuta_melilla
 */
export function detectShippingZone(postalCode = '', province = '') {
  const cp = String(postalCode).trim().replace(/\s/g, '');
  const prov = normalizeText(province);

  if (/^(35|38)\d{3}$/.test(cp)) return 'canary';
  if (/^07\d{3}$/.test(cp)) return 'balearic';
  if (/^(51|52)\d{3}$/.test(cp)) return 'ceuta_melilla';

  if (
    prov.includes('las palmas') ||
    prov.includes('santa cruz') ||
    prov.includes('tenerife') ||
    prov.includes('canarias') ||
    prov.includes('canaria')
  ) {
    return 'canary';
  }

  if (prov.includes('balear') || prov.includes('illes balears')) {
    return 'balearic';
  }

  if (prov.includes('ceuta')) return 'ceuta_melilla';
  if (prov.includes('melilla')) return 'ceuta_melilla';

  if (isHuelvaDestination(postalCode, province)) {
    return 'huelva';
  }

  if (isAndaluciaDestination(postalCode, province)) {
    return 'andalucia';
  }

  return 'peninsula';
}

/**
 * Calcula envío según zona (CP) y peso total del pedido.
 */
export function calculateShippingCostByWeight(zone, weightKg) {
  const tariffKey = ZONE_TO_TARIFF[zone] || 'peninsular';
  const tariff = SHIPPING_TARIFF_BANDS[tariffKey];
  const weight = Math.max(Number(weightKg) || 0, 0.01);

  let cost;
  let bracketLabel;
  let extraKg = 0;

  if (weight <= 2) {
    cost = tariff.upTo2Kg;
    bracketLabel = `Hasta 2 kg · ${tariff.label}`;
  } else if (weight <= 5) {
    cost = tariff.upTo5Kg;
    bracketLabel = `Hasta 5 kg · ${tariff.label}`;
  } else if (weight <= 10) {
    cost = tariff.upTo10Kg;
    bracketLabel = `Hasta 10 kg · ${tariff.label}`;
  } else {
    extraKg = Math.ceil(weight - 10);
    cost = tariff.upTo10Kg + extraKg * tariff.extraPerKg;
    bracketLabel = `Hasta 10 kg + ${extraKg} kg adicional (${tariff.extraPerKg.toFixed(2).replace('.', ',')} €/kg)`;
  }

  return {
    cost: Math.round(cost * 100) / 100,
    bracketLabel,
    pricingMethod: extraKg > 0 ? 'weight_with_supplement' : 'weight_bracket',
    tariffKey,
    extraKg,
    extraPerKg: tariff.extraPerKg,
    weightKg: weight,
    rateName: SHIPPING_ZONE_LABELS[zone] || zone
  };
}

export function formatWeightKg(kg) {
  return `${kg.toFixed(2).replace('.', ',')} kg`;
}

export function buildWeightBracketsForTariff(tariffKey) {
  const tariff = SHIPPING_TARIFF_BANDS[tariffKey];
  if (!tariff) return [];

  return [
    { maxKg: 2, cost: tariff.upTo2Kg, label: 'Hasta 2 kg' },
    { maxKg: 5, cost: tariff.upTo5Kg, label: 'Hasta 5 kg' },
    { maxKg: 10, cost: tariff.upTo10Kg, label: 'Hasta 10 kg' },
    {
      maxKg: null,
      cost: tariff.upTo10Kg,
      label: `Más de 10 kg (+${tariff.extraPerKg.toFixed(2)} €/kg)`,
      extraPerKg: tariff.extraPerKg
    }
  ];
}
